import { AxiosInstance } from 'axios';
import {
  CreateSubscriptionResult,
  Subscription,
  SubscriptionPlanApiData,
  TierKey,
} from '../../models/subscription.model';

export default class SubscriptionApi {
  readonly apiUrlPath = '/subscription';

  constructor(readonly httpClient: AxiosInstance) {}

  /** Returns all active subscription plans from the backend (source of truth). */
  async getPlans(): Promise<SubscriptionPlanApiData[]> {
    const { data } = await this.httpClient.get(`${this.apiUrlPath}/plans`);
    return data;
  }

  /**
   * Criar / alterar subscription do user.
   * Pass savedPaymentMethodPublicId to charge a saved card directly (no checkoutUrl returned).
   * Omit it to receive a checkoutUrl → frontend must open Revolut widget.
   */
  async createSubscription(
    email: string,
    tier: TierKey,
    savedPaymentMethodPublicId?: string,
  ): Promise<CreateSubscriptionResult> {
    const { data } = await this.httpClient.post(`${this.apiUrlPath}/${email}`, {
      tier,
      ...(savedPaymentMethodPublicId ? { savedPaymentMethodPublicId } : {}),
    });

    return data;
  }

  /**
   * Obter subscription activa
   */
  async getSubscription(email: string): Promise<Subscription | null> {
    const { data } = await this.httpClient.get(`${this.apiUrlPath}/${email}`);

    return data;
  }

  /**
   * Cancela a subscription mais recente em PENDING (após erro/cancelamento no widget).
   * Safe: no-op se não existir nenhuma PENDING.
   */
  async cancelPendingSubscription(email: string): Promise<void> {
    await this.httpClient.delete(`${this.apiUrlPath}/${email}/pending`);
  }

  /**
   * Returns remaining subscription limits for the current billing cycle.
   * Used to display the subscription badge in the insurance step.
   */
  async getUsage(email: string): Promise<{
    tier: string;
    coverageLimitCents: number;
    coverageUsedCents: number;
    coverageRemainingCents: number;
  }> {
    const { data } = await this.httpClient.get(`${this.apiUrlPath}/${email}/usage`);
    return data;
  }

  /**
   * Returns the Revolut widget order token for the current overdue billing cycle.
   * Used during the regularisation flow to re-pay the failed cycle's order.
   */
  async getOverdueOrderToken(email: string): Promise<{ orderToken: string }> {
    const { data } = await this.httpClient.get(`${this.apiUrlPath}/${email}/overdue-order-token`);
    return data;
  }

  async getInvoices(email: string) {
    const { data } = await this.httpClient.get(`${this.apiUrlPath}/${email}/invoices`);
    return data;
  }

  getInvoiceDownload(
    email: string,
    invoiceId: string,
  ): Promise<import('axios').AxiosResponse<ArrayBuffer>> {
    return this.httpClient.get(
      `${this.apiUrlPath}/${encodeURIComponent(email)}/invoices/${invoiceId}/download`,
      { responseType: 'arraybuffer' },
    );
  }

  downloadCreditNote(
    email: string,
    creditNoteId: string,
  ): Promise<import('axios').AxiosResponse<ArrayBuffer>> {
    return this.httpClient.get(
      `${this.apiUrlPath}/${encodeURIComponent(email)}/credit-notes/${creditNoteId}/download`,
      { responseType: 'arraybuffer' },
    );
  }
}
